import { JSConn } from "@/utilities/js-conn";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
// import { ordinalize } from "@/utilities/ordinalize";

// Notes
import { Editor, EditorContent } from "tiptap";
import TipTapDisplay from "@/components/TipTapDisplay/TipTapDisplay.vue";
import { Placeholder } from "tiptap-extensions";
import { BLANK_EDITOR_DOCUMENT } from "@/constants";
import { workOrderNotesController } from "@/controllers/workOrderNotes";
import { buildSubmitButton } from "@/utilities/form-helpers";
import { friendlyTimeStamp } from "@/utilities/display-helpers";

import { ActiveNavMenuSetting } from "@/store/modules/session";

import {
  ref,
  onMounted,
  onUnmounted,
  defineComponent,
  computed,
} from "@vue/composition-api";

export default defineComponent({
  name: "workOrderShow",
  components: {
    LoadingAnimation,
    EditorContent,
    TipTapDisplay,
  },
  props: {
    workOrderId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Please wait while the work order is loaded...");

    // Notes
    let deletingNoteId = ref("");

    let noteContentJSON = ref(BLANK_EDITOR_DOCUMENT);

    let editor = ref<any>(null);

    const localNotes = ref<any[]>([]);

    let { submitButtonText, submitting } = buildSubmitButton();
    submitButtonText.value = "Submit Note";

    const localLoadNotes = () => {
      const jsConnNotes = new JSConn();
      jsConnNotes.formParams = {
        work_order_id: props.workOrderId,
      };

      return workOrderNotesController
        .needWorkOrderNotes(jsConnNotes)
        .then(jsConn => {
          localNotes.value = jsConn.response;
        });
    };

    const deletingNote = (n: { id: string }) => {
      return n.id === deletingNoteId.value;
    };

    const deleteNote = (nt: { id: string }) => {
      deletingNoteId.value = nt.id;
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: nt.id,
      };

      Promise.all([workOrderNotesController.deleteWorkOrderNote(jsConn)]).then(
        () => {
          deletingNoteId.value = "";
          localLoadNotes();
        },
      );
    };

    const submitNote = (evt: any) => {
      evt.preventDefault();
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      const jsConn = new JSConn();
      const rightNow = new Date();
      noteContentJSON.value = editor.value.getJSON();

      jsConn.formParams = {
        content_json: noteContentJSON.value,
        work_order_id: props.workOrderId,
        created_at: rightNow,
      };

      workOrderNotesController
        .createWorkOrderNote(jsConn)
        .then(() => {
          editor.value.setContent(BLANK_EDITOR_DOCUMENT);
          ctx.root.$nextTick(() => {
            editor.value.focus();
          });

          return localLoadNotes();
        })

        .then(_jsConn => {
          submitting.value = false;
          submitButtonText.value = "Submit Note";
        });

      noteContentJSON.value = BLANK_EDITOR_DOCUMENT;
    };

    const loadWorkOrder = () => {
      const jsConnWorkOrder = new JSConn();
      const jsConnLotUnits = new JSConn();

      jsConnWorkOrder.formParams = {
        id: props.workOrderId,
      };

      const dataPromises = [
        $store.dispatch("needWorkOrder", jsConnWorkOrder),
        $store.dispatch("needLotUnits", jsConnLotUnits),
      ];

      return Promise.all(dataPromises);
    };

    const workOrder = computed(() => {
      return $store.getters.workOrder;
    });

    const isFieldWorker = computed(() => {
      return $store.getters.isFieldWorker;
    });

    const reloadWorkOrder = () => {
      // as of 2019-04-25 all the child elements are emitting an event
      // that calls this function. If different things are needed for performance
      // reasons, this can be changed to handle each event separately
      return loadWorkOrder();
    };

    onMounted(() => {
      loadWorkOrder().then(() => {
        $store.dispatch("updateActiveNavMenu", ActiveNavMenuSetting.workOrder);
        dataLoaded.value = true;
        editor.value = new Editor({
          extensions: [
            new Placeholder({
              emptyEditorClass: "is-empty",
              emptyNodeText: "Write a note...",
              showOnlyWhenEditable: true,
            }),
          ],
        });

        editor.value.setContent(BLANK_EDITOR_DOCUMENT);
      });

      localLoadNotes();
    });

    onUnmounted(() => {
      $store.dispatch("updateActiveNavMenu", ActiveNavMenuSetting.main);
    });

    return {
      dataLoaded,
      loadingMessage,

      editor,
      submitNote,
      submitting,
      submitButtonText,

      localNotes,
      friendlyTimeStamp,
      deleteNote,
      deletingNote,

      workOrder,
      isFieldWorker,

      reloadWorkOrder,
    };
  },
});
